import React from 'react';
import { gql, useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { format } from 'date-fns';
import ReminderPickerModal from '../../Modals/ReminderPickerModal';
import { eventCalendarText } from '../../../util/eventCalendarExport';
import { parseEventDate } from '../../../util/eventDate';
import { ENTITY_COLORS } from '../../../appearance/Colors';

const EVENT_FIELDS = gql`fragment EventSectionFields on Message {
    id eventArtifactId event { startDate endDate startTime endTime timeZone location rawDate needsReview }
    dueAt dueTimezone dueKind reminderAt reminderVersion reminderCancelledAt reminderFiredAt reminderMessage reminderDeliveryType
    alerts { id offsetMinutes deliveryType firedAt }
}`;
export const UPDATE_EVENT = gql`mutation UpdateEventDetails($data: UpdateMessageInput!) {
    updateMessage(data: $data) { ...EventSectionFields artifacts { id updatedAt metadata { key label value } event { startDate endDate startTime endTime timeZone location rawDate needsReview } } }
} ${EVENT_FIELDS}`;
export const ADD_REMINDERS = gql`mutation AddEventReminders($input: SetEventRemindersInput!) {
    setEventReminders(input: $input) { ...EventSectionFields }
} ${EVENT_FIELDS}`;

export const CUSTOM_REMINDERS = gql`mutation CustomizeEventReminders($input: SetMessageDueInput!) {
    setMessageDue(input: $input) { ...EventSectionFields }
} ${EVENT_FIELDS}`;
export const CLEAR_EVENT_REMINDERS = gql`mutation ClearEventReminders($messageId: ID!) {
    clearMessageDue(messageId: $messageId) { ...EventSectionFields }
} ${EVENT_FIELDS}`;

export default function EventSection({ message, readOnly, onEditReminders }) {
    const { t } = useTranslation();
    const [savedMessage, setSavedMessage] = React.useState(null);
    const originalMessage = message;
    React.useEffect(() => setSavedMessage(null), [originalMessage]);
    message = savedMessage || originalMessage;
    const event = message.event;
    const [editing, setEditing] = React.useState(false);
    const [draft, setDraft] = React.useState({});
    const [error, setError] = React.useState('');
    const [showReminders, setShowReminders] = React.useState(false);
    const [customize, customizeState] = useMutation(CUSTOM_REMINDERS);
    const [clearReminders] = useMutation(CLEAR_EVENT_REMINDERS);
    const [update, updateState] = useMutation(UPDATE_EVENT);
    const [addReminders, remindersState] = useMutation(ADD_REMINDERS);
    if (!event) return null;
    const patch = (key, value) => setDraft(previous => ({ ...previous, [key]: value }));
    const dateParts = parseEventDate(event.startDate || event.rawDate);
    const formattedDateStr = dateParts
        ? format(dateParts.date, 'EEE, MMM d, yyyy')
        : (event.startDate || event.rawDate || '');
    const endDateParts = parseEventDate(event.endDate);
    const formattedEndDate = endDateParts
        ? format(endDateParts.date, 'EEE, MMM d, yyyy')
        : event.endDate;
    const formattedDateRange = event.endDate && event.endDate !== event.startDate
        ? `${formattedDateStr} – ${formattedEndDate}`
        : formattedDateStr;
    let timingLabel = '';
    let isPast = false;
    if (dateParts) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const target = new Date(dateParts.date);
        target.setHours(0, 0, 0, 0);
        const diffDays = Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        if (diffDays < 0) {
            timingLabel = t('messages:passed', 'passed');
            isPast = true;
        } else if (diffDays === 0) {
            timingLabel = t('messages:today', 'today');
        } else if (diffDays === 1) {
            timingLabel = t('messages:tomorrow', 'tomorrow');
        } else {
            timingLabel = t('messages:inDays', { count: diffDays, defaultValue: `in ${diffDays} days` });
        }
    }
    const save = async () => {
        setError('');
        try {
            const input = {};
            ['startDate', 'endDate', 'startTime', 'endTime', 'timeZone', 'location', 'rawDate'].forEach(key => { if (draft[key]) input[key] = draft[key]; });
            const result = await update({ variables: { data: { messageId: message.id, content: { event: input } } } });
            setSavedMessage({ ...message, ...result.data.updateMessage });
            setEditing(false);
        } catch (_) { setError(t('messages:eventSaveFailed')); }
    };
    const add = async () => {
        setError('');
        try { const result = await addReminders({ variables: { input: { messageId: message.id, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone } } }); setSavedMessage({ ...message, ...result.data.setEventReminders }); }
        catch (_) { setError(t('messages:eventRemindersFailed')); }
    };
    const exportEvent = () => {
        const url = URL.createObjectURL(new Blob([eventCalendarText(message)], { type: 'text/calendar;charset=utf-8' }));
        const anchor = document.createElement('a'); anchor.href = url; anchor.download = 'event.ics'; anchor.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    };
    return <Panel onClick={ev => ev.stopPropagation()} onMouseDown={ev => ev.stopPropagation()}>
        <strong><span aria-hidden="true">📅 </span>{t('messages:event')}</strong>
        <div>
            {event.needsReview ? (
                event.rawDate || t('messages:eventsNeedDate')
            ) : (
                <>
                    <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                        {formattedDateRange} · {event.startTime || t('messages:allDay')}{event.endTime ? `–${event.endTime}` : ''}
                    </span>
                    {timingLabel ? (
                        <span style={{
                            color: !isPast ? (ENTITY_COLORS.event?.text || '#e8b96b') : 'rgba(255, 255, 255, 0.65)',
                            fontWeight: !isPast ? 600 : 'normal',
                            marginLeft: 6
                        }}>
                            ({timingLabel})
                        </span>
                    ) : null}
                </>
            )}
        </div>
        {event.timeZone && <small>{event.timeZone}</small>}
        {event.location && <div><span aria-hidden="true">📍 </span>{event.location}</div>}
        {event.needsReview && <small>{t('messages:checkEventDate')}</small>}
        <Actions>
            {!readOnly && <button type="button" onClick={() => { setDraft({ ...event }); setEditing(!editing); }}>{t('messages:editEvent')}</button>}
            {!readOnly && !event.needsReview && <button type="button" disabled={remindersState?.loading} onClick={message.reminderAt ? (onEditReminders || (() => setShowReminders(true))) : add}>{message.reminderAt ? t('messages:editEventReminders') : t('messages:addEventReminders')}</button>}
            {!event.needsReview && <button type="button" onClick={exportEvent}>{t('messages:addToCalendar')}</button>}
        </Actions>
        {editing && <form onSubmit={ev => { ev.preventDefault(); save(); }}>
            <label>{t('messages:eventDateFrom')}<input type="date" value={draft.startDate || ''} onChange={ev => patch('startDate', ev.target.value)} /></label>
            <label>{t('messages:eventDateTo')}<input type="date" min={draft.startDate || undefined} value={draft.endDate || ''} onChange={ev => patch('endDate', ev.target.value)} /></label>
            <label><input type="checkbox" checked={!draft.startTime} onChange={ev => setDraft(previous => ({ ...previous, startTime: ev.target.checked ? '' : '09:00', endTime: '' }))} /> {t('messages:allDay')}</label>
            {draft.startTime && <>
                <label>{t('messages:eventStartTime')}<input type="time" value={draft.startTime} onChange={ev => patch('startTime', ev.target.value)} /></label>
                <label>{t('messages:eventEndTime')}<input type="time" value={draft.endTime || ''} onChange={ev => patch('endTime', ev.target.value)} /></label>
            </>}
            <label>{t('messages:eventTimezone')}<input value={draft.timeZone || ''} placeholder={Intl.DateTimeFormat().resolvedOptions().timeZone} onChange={ev => patch('timeZone', ev.target.value)} /></label>
            <label>{t('messages:eventLocation')}<input value={draft.location || ''} onChange={ev => patch('location', ev.target.value)} /></label>
            {message.reminderAt && <small>{t('messages:eventEditClearsAlerts')}</small>}
            <Actions><button disabled={updateState?.loading} type="submit">{t('common:save')}</button><button type="button" onClick={() => setEditing(false)}>{t('common:cancel')}</button></Actions>
        </form>}
        {!message.reminderAt && !event.needsReview && !readOnly && <small>{t('messages:eventReminderDefaults')}</small>}
        {error && <div role="alert">{error}</div>}
        <ReminderPickerModal isOpen={showReminders} initialReminderAt={message.reminderAt}
            initialDeliveryType={message.reminderDeliveryType} initialReminderMessage={message.reminderMessage} initialAlerts={message.alerts}
            loading={customizeState?.loading} onClose={() => setShowReminders(false)}
            onSubmit={async ({ reminderAt, alerts, reminderMessage }) => {
                try {
                    const result = await customize({ variables: { input: { messageId: message.id,
                        dueAt: reminderAt.toISOString(), dueKind: 'event',
                        dueTimezone: message.dueTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
                        alerts, reminderMessage } } });
                    setSavedMessage({ ...message, ...result.data.setMessageDue }); setShowReminders(false);
                } catch (_) { setError(t('messages:eventRemindersFailed')); }
            }}
            onClearReminder={async () => {
                try { const result = await clearReminders({ variables: { messageId: message.id } });
                    setSavedMessage({ ...message, ...result.data.clearMessageDue }); setShowReminders(false);
                } catch (_) { setError(t('messages:eventRemindersFailed')); }
            }} />
    </Panel>;
}
const Panel = styled.section`
    margin: 8px 0;
    padding: 14px;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    display: grid;
    gap: 8px;
    font-size: 13px;
    line-height: 1.4;
    small { opacity: 0.7; }
    form { display: grid; gap: 8px; }
    label { display: grid; gap: 4px; }
    input { color: inherit; background: transparent; padding: 6px; border: 1px solid rgba(255, 255, 255, 0.16); border-radius: 6px; }
`;
const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    button {
        cursor: pointer;
        color: rgba(255, 255, 255, 0.9);
        font: inherit;
        font-size: 12px;
        font-weight: 500;
        background: rgba(255, 255, 255, 0.08);
        border: 1px solid transparent;
        padding: 6px 12px;
        border-radius: 8px;
        transition: background 0.15s ease;
        &:hover { background: rgba(255, 255, 255, 0.14); }
        &:disabled { opacity: 0.55; cursor: default; }
    }
`;
